/**
 * OrgTreeView — the STRUCTURAL org chart (company → function → department
 * → team → people, arbitrary depth). This is THE org chart rendered on
 * `/hr/org-chart` (gated by `flags.hrOrgChart`).
 *
 * A compact top-down organization chart: each node is a small text card
 * (no avatar) with a colored left accent per level, name-first /
 * position-second copy, and a fixed min/max width with ellipsis truncation
 * so long names never blow out the box. Children fan out beneath a parent
 * connected by CSS connector lines (no layout library, so it handles any
 * depth). Teams are leaf cards; members expand as a single-column list.
 * Motion respects `prefers-reduced-motion`. Structural only — never renders
 * approver lines.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import type { OrgChartEmployee } from '@lms/shared-types'
import type {
  OrgTree,
  OrgTreeTeamNode,
  OrgTreeUnitNode,
} from '../../../../lib/org-chart'
import { RowActionsMenu, type RowAction } from '../../../primitives/RowActionsMenu'

const UNIT_TYPE_LABEL: Record<OrgTreeUnitNode['unitType'], string> = {
  company: 'Company',
  function: 'Function',
  department: 'Department',
}

/** Resolves an employee id to their primary approver (or null when they are
 *  top-of-org). Supplied by the page from the org-chart index; defaults to
 *  "no approver" so the component renders standalone in tests. */
type ApproverResolver = (employeeId: string) => OrgChartEmployee | null

/** A pickable approver in the "Change approver" control. */
export interface ApproverCandidate {
  id: string
  name: string
  jobTitle: string
}

/** Derived search highlight state. When `active`, non-matching cards/members
 *  are dimmed and matched teams are force-expanded. */
interface SearchState {
  active: boolean
  matchIds: Set<string> // matching employees (members + unit heads)
  teamHasMatch: Set<string> // teams containing a match
  unitOnPath: Set<string> // units on the root→match path
}
const EMPTY_SEARCH: SearchState = {
  active: false,
  matchIds: new Set(),
  teamHasMatch: new Set(),
  unitOnPath: new Set(),
}

const ZOOM_MIN = 0.4
const ZOOM_MAX = 1.5
const ZOOM_STEP = 0.1

interface OrgChartCtx {
  resolveApprover: ApproverResolver
  /** Open the approver popover for a member, anchored to their row. */
  onSelectMember: (emp: OrgChartEmployee, anchor: DOMRect) => void
  /** Candidates for the approver picker. Empty = editing disabled. */
  candidates: ApproverCandidate[]
  /** Persist a new primary approver. Absent = read-only (no edit control). */
  onSetApprover?: (employeeId: string, approverId: string) => Promise<void>
  /** Search highlight/dim state derived from the toolbar query. */
  search: SearchState
  /** Bulk expand/collapse broadcast for team member lists. `epoch` bumps on
   *  each toolbar click so teams re-apply `open` even after the user has
   *  toggled one individually. */
  bulk: { open: boolean; epoch: number }
  /** ⋮ quick-action items for a unit box. Absent = no menu (read-only). */
  unitActions?: (unit: OrgTreeUnitNode) => RowAction[]
  /** ⋮ quick-action items for a team box. Absent = no menu (read-only). */
  teamActions?: (team: OrgTreeTeamNode) => RowAction[]
  /** Teams pickable in the popover's "Change team" control. */
  teamCandidates: ApproverCandidate[]
  /** Persist a person's new team. Absent = the popover shows no team edit. */
  onSetTeam?: (employeeId: string, teamId: string) => Promise<void>
  /** Move a person to the 'Unassigned' holder team. Absent = no "Remove
   *  from team" control. */
  onRemoveFromTeam?: (employeeId: string) => Promise<void>
  /** The 'Unassigned' holder team id — "Remove from team" is hidden for a
   *  person already in it. */
  unassignedTeamId?: string
}
const OrgChartContext = createContext<OrgChartCtx>({
  resolveApprover: () => null,
  onSelectMember: () => {},
  candidates: [],
  search: EMPTY_SEARCH,
  bulk: { open: false, epoch: 0 },
  teamCandidates: [],
})

// Connector-line org-chart CSS (the nested-<ul> technique) + compact card
// styling. Scoped <style> is the pragmatic fit — connectors and media
// queries can't be inline styles.
const CHART_STYLES = `
/* Search toolbar — sticky to the top of the scroll panel. */
.org-chart-toolbar {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -16px -16px 8px;
  padding: 10px 16px;
  background: color-mix(in srgb, var(--surface) 92%, transparent);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid var(--border);
}
.org-chart-search { font-size: 13px; opacity: 0.7; }
.org-chart-search__input {
  flex: 0 1 280px;
  font: inherit;
  font-size: 13px;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}
.org-chart-search__count { font-size: 11px; color: var(--text-3); }
/* Link-style toggle sitting right beside the search field. */
.org-chart-search__toggle {
  appearance: none;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--steel-blue);
  background: none;
  border: none;
  padding: 0 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  outline: none;
}
/* Underline the label only — never the caret. */
.org-chart-search__toggle:hover .org-chart-search__toggle-label {
  text-decoration: underline;
}
.org-chart-search__caret {
  font-size: 13px;
  display: inline-block;
  transition: transform 120ms ease;
}

/* View toggle — segmented control; starts the right-hand toolbar cluster. */
.org-chart-viewtoggle {
  margin-left: auto;
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
}
.org-chart-viewtoggle__btn {
  appearance: none;
  border: none;
  background: var(--surface);
  color: var(--text-2);
  font-size: 12px;
  font-family: inherit;
  height: 26px;
  padding: 0 10px;
  cursor: pointer;
  transition: background 100ms ease, color 100ms ease;
}
.org-chart-viewtoggle__btn + .org-chart-viewtoggle__btn {
  border-left: 1px solid var(--border);
}
.org-chart-viewtoggle__btn.is-active {
  background: var(--steel-blue);
  color: #fff;
  font-weight: 600;
}

/* Zoom controls — segmented cluster to the right of the view toggle. */
.org-chart-zoom {
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.org-chart-zoom__btn,
.org-chart-zoom__pct,
.org-chart-zoom__fit {
  appearance: none;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-2);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  height: 26px;
  border-radius: 6px;
  transition: background 100ms ease, border-color 100ms ease;
}
.org-chart-zoom__btn {
  width: 26px;
  font-size: 16px;
  line-height: 1;
}
.org-chart-zoom__pct {
  min-width: 46px;
  font-variant-numeric: tabular-nums;
}
.org-chart-zoom__fit {
  padding: 0 10px;
  color: var(--steel-blue);
}
.org-chart-zoom__full { white-space: nowrap; }
.org-chart-zoom__btn:hover:not(:disabled),
.org-chart-zoom__pct:hover,
.org-chart-zoom__fit:hover {
  background: var(--primary-bg);
  border-color: var(--steel-blue);
}
.org-chart-zoom__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Zoomable canvas — shrink-wraps its content so offsetWidth is the true
   (unscaled) content box; the sized wrapper around it mirrors scale × box so
   the scroll region tracks the zoom. */
.org-chart-canvas {
  width: max-content;
  transition: transform 80ms ease-out;
}
@media (prefers-reduced-motion: reduce) {
  .org-chart-canvas { transition: none; }
}

.org-chart { --line: color-mix(in srgb, var(--text-3) 70%, var(--border)); }
.org-chart { display: inline-block; min-width: 100%; text-align: center; }
.org-chart ul {
  display: flex;
  justify-content: center;
  padding: 16px 0 0;
  margin: 0;
  list-style: none;
  position: relative;
}
.org-chart li {
  position: relative;
  padding: 16px 8px 0;
  list-style: none;
}
.org-chart li::before,
.org-chart li::after {
  content: '';
  position: absolute;
  top: 0;
  right: 50%;
  width: 50%;
  height: 16px;
  border-top: 1.5px solid var(--line);
}
.org-chart li::after {
  right: auto;
  left: 50%;
  border-left: 1.5px solid var(--line);
}
.org-chart li:only-child::before,
.org-chart li:only-child::after { display: none; }
.org-chart li:only-child { padding-top: 16px; }
.org-chart li:first-child::before,
.org-chart li:last-child::after { border: 0 none; }
.org-chart li:last-child::before {
  border-right: 1.5px solid var(--line);
  border-radius: 0 6px 0 0;
}
.org-chart li:first-child::after { border-radius: 6px 0 0 0; }
.org-chart ul ul::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 0;
  height: 16px;
  border-left: 1.5px solid var(--line);
}
/* Roots have no parent, so the top-level list must NOT draw the sibling
   connector bar — otherwise multiple roots look like they hang off a phantom
   shared parent. Each root renders as its own standalone tree. */
.org-chart > ul > li::before,
.org-chart > ul > li::after { display: none; }
.org-chart > ul > li { padding-top: 0; }

/* ── Left-to-right layout ──────────────────────────────────────────────────
   Same nodes, rotated 90°: each li lays its card and its children side-by-side
   (row), children stack vertically (column), connected by an elbow "spine"
   down the left of each child list (mind-map / file-tree style). Turns width
   into height.

   The base top-down connectors use :first-child/:last-child/:only-child rules
   at (0,2,2) specificity, so the overrides below MUST repeat those selectors
   (matching specificity, declared later so they win) — a plain
   .org-chart--lr li::before at (0,1,2) would lose and leave stray borders. */
.org-chart--lr { text-align: left; }
.org-chart--lr ul {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0 0 0 32px;   /* gutter: 16px trunk + 16px tick */
}
.org-chart--lr li {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 0;
}
/* kill the top-down vertical drop-line (parent card → sibling bus). */
.org-chart--lr ul ul::before { display: none; }

/* horizontal tick: spine (gutter mid) → this node's card. */
.org-chart--lr li::before,
.org-chart--lr li:first-child::before,
.org-chart--lr li:last-child::before,
.org-chart--lr li:only-child::before {
  content: '';
  display: block;
  position: absolute;
  left: -16px;
  right: auto;
  top: 50%;
  bottom: auto;
  width: 16px;
  height: 0;
  border: 0 none;
  border-top: 1.5px solid var(--line);
  border-radius: 0;
}
/* vertical spine at the gutter midpoint, linking siblings. */
.org-chart--lr li::after,
.org-chart--lr li:first-child::after,
.org-chart--lr li:last-child::after {
  content: '';
  display: block;
  position: absolute;
  left: -16px;
  right: auto;
  top: 0;
  bottom: 0;
  width: 0;
  height: auto;
  border: 0 none;
  border-left: 1.5px solid var(--line);
  border-radius: 0;
}
/* trim the spine so it only spans first→last child mid-points. */
.org-chart--lr li:first-child::after { top: 50%; bottom: 0; }
.org-chart--lr li:last-child::after { top: 0; bottom: 50%; }
.org-chart--lr li:only-child::after { display: none; }
.org-chart--lr li:only-child { padding: 6px 0; }

/* explicit trunk: parent card's right edge → the spine (the other gutter
   half), at the parent's vertical centre. Guarantees the parent→children
   link regardless of card width / vertical centring. :has() detects a card
   that owns a child list. */
.org-chart--lr li:has(> ul) > .org-chart__node { position: relative; }
.org-chart--lr li:has(> ul) > .org-chart__node::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 100%;
  width: 16px;
  border-top: 1.5px solid var(--line);
}

/* the roots have no parent → no self-connectors, no left gutter (but the
   root card KEEPS its trunk, to reach its own children). */
.org-chart--lr > ul { padding-left: 0; }
.org-chart--lr > ul > li::before,
.org-chart--lr > ul > li::after { display: none; }

.org-chart__node {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  box-sizing: border-box;
  min-width: 150px;
  max-width: 190px;
  padding: 8px 12px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--a1) 12%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--a1) 40%, var(--border));
  border-left: 3px solid var(--a1);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05),
              0 2px 10px color-mix(in srgb, var(--a1) 22%, transparent);
  transition: transform 140ms ease, box-shadow 140ms ease,
              background 140ms ease, opacity 140ms ease;
  text-align: left;
}
.org-chart__node:hover {
  transform: translateY(-2px);
  background: color-mix(in srgb, var(--a1) 18%, var(--surface));
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08),
              0 12px 26px color-mix(in srgb, var(--a1) 32%, transparent);
}
/* Search dim/highlight. */
.org-chart__node.is-dimmed { opacity: 0.28; }
.org-chart__node.is-match {
  box-shadow: 0 0 0 2px var(--a1),
              0 2px 10px color-mix(in srgb, var(--a1) 30%, transparent);
}
.org-chart__eyebrow {
  max-width: 100%;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--a1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-chart__name {
  max-width: 100%;
  font-size: 12.5px;
  font-weight: 650;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-chart__position {
  max-width: 100%;
  font-size: 10.5px;
  color: var(--text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ⋮ quick-action menu — pinned to the card's top-right. The card reserves
   right padding so its text never runs under the trigger. The trigger scales
   down to suit these compact cards without touching RowActionsMenu's markup. */
.org-chart__node--has-menu { padding-right: 34px; }
.org-chart__menu {
  position: absolute;
  top: 2px;
  right: 0;
  z-index: 3;
  transform: scale(0.82);
  transform-origin: top right;
}

/* Per-level accent (left bar + tint + eyebrow) — bright, vivid hues. */
.org-chart__node--company    { --a1: #8b5cf6; }
.org-chart__node--function   { --a1: #3b82f6; }
.org-chart__node--department  { --a1: #f59e0b; }
.org-chart__node--team       { --a1: #06b6d4; }

.org-chart summary {
  list-style: none;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
  color: var(--a1);
  min-height: 20px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 1px;
}
.org-chart summary::-webkit-details-marker { display: none; }
.org-chart summary::after {
  /* U+25B8 + U+FE0E (text presentation) so it inherits the summary's
     colour instead of rendering as a dark emoji glyph on macOS. */
  content: '\\25B8\\FE0E';
  color: inherit;
  font-size: 13px;
  display: inline-block;
  transition: transform 120ms ease;
}
.org-chart details[open] summary::after { transform: rotate(90deg); }
.org-chart summary:focus-visible {
  outline: 2px solid var(--focus, #4c7dff);
  outline-offset: 2px;
  border-radius: 4px;
}
.org-chart__members {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1px;
  padding-top: 4px;
  animation: org-chart-reveal 140ms ease;
}
.org-chart__member {
  appearance: none;
  border: 0;
  width: 100%;
  text-align: left;
  font: inherit;
  font-size: 11px;
  color: var(--text-2);
  background: transparent;
  padding: 2px 4px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background 120ms ease, opacity 120ms ease;
}
.org-chart__member:hover { background: color-mix(in srgb, var(--a1) 12%, transparent); color: var(--text); }
.org-chart__member:focus-visible {
  outline: 2px solid var(--focus, #4c7dff);
  outline-offset: 1px;
}
.org-chart__member.is-dimmed { opacity: 0.3; }
.org-chart__member.is-match {
  background: color-mix(in srgb, var(--a1) 22%, transparent);
  color: var(--text);
  font-weight: 600;
}

/* Approver popover. */
.org-chart-popover__backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: transparent;
}
.org-chart-popover {
  position: fixed;
  z-index: 41;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12),
              0 16px 40px rgba(15, 23, 42, 0.2);
  text-align: left;
  animation: org-chart-reveal 120ms ease;
}
.org-chart-popover__head {
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--border);
}
.org-chart-popover__name { font-size: 13px; font-weight: 650; color: var(--text); }
.org-chart-popover__title { font-size: 11px; color: var(--text-2); }
.org-chart-popover__label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-3);
  margin-bottom: 3px;
}
/* Divider between the Approver block and the Team block in the popover. */
.org-chart-popover__section {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}
.org-chart-popover__approver { font-size: 13px; color: var(--text); display: flex; flex-direction: column; gap: 1px; }
.org-chart-popover__none { font-size: 12px; color: var(--text-3); font-style: italic; }
.org-chart-popover__close {
  position: absolute;
  top: 6px;
  right: 8px;
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-3);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
}
.org-chart-popover__close:hover { background: color-mix(in srgb, var(--text-3) 15%, transparent); color: var(--text); }
.org-chart-popover__change {
  margin-top: 10px;
  appearance: none;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-2);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 8px;
  transition: background 120ms ease, border-color 120ms ease;
}
.org-chart-popover__change:hover {
  border-color: color-mix(in srgb, #3b82f6 50%, var(--border));
  color: var(--text);
}
.org-chart-popover__change:disabled { opacity: 0.5; cursor: default; }
/* Row holding "Change team" + "Remove from team" side by side. */
.org-chart-popover__teamactions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.org-chart-popover__teamactions .org-chart-popover__change { margin-top: 0; }
.org-chart-popover__change--danger {
  color: var(--danger-fg, #dc2626);
}
.org-chart-popover__change--danger:hover {
  border-color: color-mix(in srgb, var(--danger-fg, #dc2626) 50%, var(--border));
  color: var(--danger-fg, #dc2626);
}
.org-chart-popover__edit { display: flex; flex-direction: column; gap: 8px; }
.org-chart-popover__select {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
}
.org-chart-popover__ac { position: relative; }
.org-chart-popover__aclist {
  position: absolute;
  z-index: 1;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 176px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.16);
}
.org-chart-popover__acopt {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.org-chart-popover__acopt.is-active,
.org-chart-popover__acopt:hover {
  background: color-mix(in srgb, #3b82f6 14%, transparent);
}
.org-chart-popover__acname { font-size: 12px; color: var(--text); }
.org-chart-popover__error { font-size: 11px; color: var(--danger-fg, #dc2626); }
.org-chart-popover__actions { display: flex; justify-content: flex-end; gap: 6px; }
.org-chart-popover__btn {
  appearance: none;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 8px;
}
.org-chart-popover__btn:disabled { opacity: 0.5; cursor: default; }
.org-chart-popover__btn--primary {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}
.org-chart-popover__btn--ghost { background: transparent; color: var(--text-2); }
@keyframes org-chart-reveal {
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .org-chart__node, .org-chart__member { transition: none; }
  .org-chart__node:hover { transform: none; }
  .org-chart__members { animation: none; }
}

/* "Unassigned teams" — a self-contained card below the chart. A discrete
   dashed card (rather than a full-width divider) so it reads as one group
   and needs no line spanning the horizontally-scrolling panel. */
.org-tree-unassigned {
  display: inline-block;
  margin-top: 24px;
  padding: 12px 14px;
  border: 1px dashed var(--border);
  border-radius: 12px;
  background: color-mix(in srgb, var(--text-3) 5%, transparent);
}
.org-tree-unassigned__title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-2);
  margin-bottom: 12px;
  text-align: left;
}
.org-tree-unassigned__hint { font-size: 11px; font-weight: 400; color: var(--text-3); }
/* Descendant selector out-specifies .org-chart's inline-block/centering. */
.org-tree-unassigned .org-tree-unassigned__cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
  text-align: left;
}
`

function MemberRow({ emp }: { emp: OrgChartEmployee }): JSX.Element {
  const { onSelectMember, search } = useContext(OrgChartContext)
  const matched = search.active && search.matchIds.has(emp.id)
  const dimmed = search.active && !matched
  return (
    <button
      type="button"
      className={
        'org-chart__member' +
        (matched ? ' is-match' : '') +
        (dimmed ? ' is-dimmed' : '')
      }
      data-testid={`org-tree-member-${emp.id}`}
      aria-haspopup="dialog"
      onClick={(e) =>
        onSelectMember(emp, e.currentTarget.getBoundingClientRect())
      }
    >
      {emp.name}
    </button>
  )
}

function Members({ members }: { members: OrgChartEmployee[] }): JSX.Element {
  return (
    <div className="org-chart__members">
      {members.map((emp) => (
        <MemberRow key={emp.id} emp={emp} />
      ))}
    </div>
  )
}

interface Selection {
  emp: OrgChartEmployee
  anchor: DOMRect
}

/** Typeahead approver picker — filters candidates as you type instead of
 *  listing everyone at once. Keyboard: ↑/↓ to move, Enter to pick, Esc to
 *  close the list. Selecting a name sets `value`; typing clears it so Save
 *  stays disabled until a real candidate is chosen. */
function ApproverAutocomplete({
  candidates,
  excludeId,
  value,
  onChange,
  disabled,
  inputTestId,
  optionTestIdPrefix,
}: {
  candidates: ApproverCandidate[]
  excludeId: string
  value: string
  onChange: (id: string) => void
  disabled: boolean
  inputTestId: string
  optionTestIdPrefix: string
}): JSX.Element {
  const options = useMemo(
    () => candidates.filter((c) => c.id !== excludeId),
    [candidates, excludeId],
  )
  const [query, setQuery] = useState(
    () => options.find((o) => o.id === value)?.name ?? '',
  )
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const base =
      q.length === 0
        ? options
        : options.filter(
            (o) =>
              o.name.toLowerCase().includes(q) ||
              o.jobTitle.toLowerCase().includes(q),
          )
    return base.slice(0, 50)
  }, [options, q])

  function choose(c: ApproverCandidate): void {
    onChange(c.id)
    setQuery(c.name)
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(a + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      const pick = filtered[active]
      if (open && pick) {
        e.preventDefault()
        choose(pick)
      }
    } else if (e.key === 'Escape' && open) {
      // Close the list without closing the whole popover.
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div className="org-chart-popover__ac">
      <input
        type="text"
        className="org-chart-popover__select"
        data-testid={inputTestId}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Type a name…"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          setActive(0)
          if (value) onChange('') // typing invalidates the prior pick
        }}
        onFocus={() => setOpen(true)}
        // Close the list on click/tab away. Option rows use onMouseDown +
        // preventDefault so a pick registers before this blur fires.
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {open && filtered.length > 0 ? (
        <ul className="org-chart-popover__aclist" role="listbox">
          {filtered.map((c, i) => (
            <li
              key={c.id}
              role="option"
              aria-selected={c.id === value}
              data-testid={`${optionTestIdPrefix}${c.id}`}
              className={
                'org-chart-popover__acopt' + (i === active ? ' is-active' : '')
              }
              onMouseDown={(e) => {
                e.preventDefault() // keep input focus; register before blur
                choose(c)
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="org-chart-popover__acname">{c.name}</span>
              {c.jobTitle ? (
                <span className="org-chart-popover__title">{c.jobTitle}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Floating popover showing the selected person's approver. Positioned
 *  fixed to the anchored row; closes on Escape, outside click, or scroll. */
function ApproverPopover({
  selection,
  resolveApprover,
  onClose,
}: {
  selection: Selection
  resolveApprover: ApproverResolver
  onClose: () => void
}): JSX.Element {
  const { emp, anchor } = selection
  const {
    candidates,
    onSetApprover,
    teamCandidates,
    onSetTeam,
    onRemoveFromTeam,
    unassignedTeamId,
  } = useContext(OrgChartContext)
  const approver = resolveApprover(emp.id)
  const canEdit = Boolean(onSetApprover) && candidates.length > 0
  const canEditTeam = Boolean(onSetTeam) && teamCandidates.length > 0
  const canRemoveFromTeam =
    Boolean(onRemoveFromTeam) && emp.teamId !== unassignedTeamId

  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [editing, setEditing] = useState(false)
  const [choice, setChoice] = useState<string>(approver?.id ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Team editor — independent from the approver editor above.
  const [editingTeam, setEditingTeam] = useState(false)
  const [teamChoice, setTeamChoice] = useState<string>('')
  const [teamSaving, setTeamSaving] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [removing, setRemoving] = useState(false)

  async function handleSave(): Promise<void> {
    if (!onSetApprover || !choice || choice === emp.id) return
    setSaving(true)
    setError(null)
    try {
      await onSetApprover(emp.id, choice)
      onClose() // page invalidates the query; the tree re-renders fresh
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save approver.')
      setSaving(false)
    }
  }

  async function handleSaveTeam(): Promise<void> {
    if (!onSetTeam || !teamChoice || teamChoice === emp.teamId) return
    setTeamSaving(true)
    setTeamError(null)
    try {
      await onSetTeam(emp.id, teamChoice)
      onClose() // page invalidates the query; the tree re-renders fresh
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'Could not save team.')
      setTeamSaving(false)
    }
  }

  async function handleRemoveFromTeam(): Promise<void> {
    if (!onRemoveFromTeam) return
    setRemoving(true)
    setTeamError(null)
    try {
      await onRemoveFromTeam(emp.id)
      onClose() // page invalidates the query; the tree re-renders fresh
    } catch (err) {
      setTeamError(
        err instanceof Error ? err.message : 'Could not remove from team.',
      )
      setRemoving(false)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    const onScroll = (e: Event): void => {
      // Only close when the page/chart behind scrolls (the fixed popover
      // would drift). Scrolling *inside* the popover — e.g. the approver
      // autocomplete list — must NOT close it.
      const target = e.target as Node | null
      if (target && popoverRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    // Capture phase so a scroll anywhere (incl. the chart panel) closes it,
    // since the popover is fixed-positioned and would otherwise drift.
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [onClose])

  const width = 232
  // Prefer placing the popover just to the right of the row; flip to the left
  // if it would overflow the viewport. Vertically align to the row, clamped.
  const gap = 8
  const left =
    anchor.right + gap + width <= window.innerWidth - 8
      ? anchor.right + gap
      : Math.max(8, anchor.left - gap - width)
  const top = Math.max(
    8,
    Math.min(anchor.top, window.innerHeight - 140),
  )

  // Portal to <body>: a transformed/filtered ancestor (the app layout) would
  // otherwise become the containing block for our position:fixed popover and
  // throw the coordinates off. Rendering at the body root keeps `fixed`
  // relative to the viewport, matching getBoundingClientRect().
  return createPortal(
    <>
      <div
        className="org-chart-popover__backdrop"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={`Approver for ${emp.name}`}
        className="org-chart-popover"
        data-testid={`org-tree-approver-${emp.id}`}
        style={{ top, left, width }}
      >
        <div className="org-chart-popover__head">
          <div className="org-chart-popover__name">{emp.name}</div>
          <div className="org-chart-popover__title">{emp.jobTitle}</div>
        </div>
        <div className="org-chart-popover__label">Approver</div>
        {editing ? (
          <div className="org-chart-popover__edit">
            <ApproverAutocomplete
              candidates={candidates}
              excludeId={emp.id}
              value={choice}
              onChange={setChoice}
              disabled={saving}
              inputTestId={`org-tree-approver-input-${emp.id}`}
              optionTestIdPrefix="org-tree-approver-option-"
            />
            {error ? (
              <div className="org-chart-popover__error">{error}</div>
            ) : null}
            <div className="org-chart-popover__actions">
              <button
                type="button"
                className="org-chart-popover__btn org-chart-popover__btn--ghost"
                onClick={() => {
                  setEditing(false)
                  setError(null)
                  setChoice(approver?.id ?? '')
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="org-chart-popover__btn org-chart-popover__btn--primary"
                data-testid={`org-tree-approver-save-${emp.id}`}
                onClick={handleSave}
                disabled={saving || !choice || choice === emp.id}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {approver ? (
              <div className="org-chart-popover__approver">
                <b>{approver.name}</b>
                {approver.jobTitle ? (
                  <span className="org-chart-popover__title">
                    {approver.jobTitle}
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="org-chart-popover__none">
                Top of org — no approver
              </div>
            )}
            {canEdit ? (
              <button
                type="button"
                className="org-chart-popover__change"
                data-testid={`org-tree-approver-edit-${emp.id}`}
                onClick={() => setEditing(true)}
              >
                Change approver
              </button>
            ) : null}
          </>
        )}

        {canEditTeam || canRemoveFromTeam ? (
          <div className="org-chart-popover__section">
            <div className="org-chart-popover__label">Team</div>
            {editingTeam ? (
              <div className="org-chart-popover__edit">
                <ApproverAutocomplete
                  candidates={teamCandidates}
                  excludeId={emp.teamId}
                  value={teamChoice}
                  onChange={setTeamChoice}
                  disabled={teamSaving}
                  inputTestId={`org-tree-team-input-${emp.id}`}
                  optionTestIdPrefix="org-tree-team-option-"
                />
                {teamError ? (
                  <div className="org-chart-popover__error">{teamError}</div>
                ) : null}
                <div className="org-chart-popover__actions">
                  <button
                    type="button"
                    className="org-chart-popover__btn org-chart-popover__btn--ghost"
                    onClick={() => {
                      setEditingTeam(false)
                      setTeamError(null)
                      setTeamChoice('')
                    }}
                    disabled={teamSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="org-chart-popover__btn org-chart-popover__btn--primary"
                    data-testid={`org-tree-team-save-${emp.id}`}
                    onClick={handleSaveTeam}
                    disabled={teamSaving || !teamChoice || teamChoice === emp.teamId}
                  >
                    {teamSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="org-chart-popover__approver">
                  <b>{emp.teamName}</b>
                </div>
                {teamError ? (
                  <div className="org-chart-popover__error">{teamError}</div>
                ) : null}
                <div className="org-chart-popover__teamactions">
                  {canEditTeam ? (
                    <button
                      type="button"
                      className="org-chart-popover__change"
                      data-testid={`org-tree-team-edit-${emp.id}`}
                      onClick={() => setEditingTeam(true)}
                      disabled={removing}
                    >
                      Change team
                    </button>
                  ) : null}
                  {canRemoveFromTeam ? (
                    <button
                      type="button"
                      className="org-chart-popover__change org-chart-popover__change--danger"
                      data-testid={`org-tree-team-remove-${emp.id}`}
                      onClick={handleRemoveFromTeam}
                      disabled={removing}
                    >
                      {removing ? 'Removing…' : 'Remove from team'}
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}
        <button
          type="button"
          className="org-chart-popover__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </>,
    document.body,
  )
}

function TeamCard({ team }: { team: OrgTreeTeamNode }): JSX.Element {
  const { search, bulk, teamActions } = useContext(OrgChartContext)
  const count = team.members.length
  const hasMatch = search.active && search.teamHasMatch.has(team.id)
  const dimmed = search.active && !hasMatch
  const actions = teamActions?.(team)

  // Controlled <details>: search force-opens a matched team; the toolbar
  // Expand/Collapse-all broadcast resets every team; otherwise the user's own
  // toggle wins.
  const [userOpen, setUserOpen] = useState(false)
  useEffect(() => {
    setUserOpen(bulk.open)
  }, [bulk.epoch, bulk.open])
  const open = hasMatch || userOpen

  return (
    <div
      className={
        'org-chart__node org-chart__node--team' +
        (dimmed ? ' is-dimmed' : '') +
        (actions && actions.length > 0 ? ' org-chart__node--has-menu' : '')
      }
      data-testid={`org-tree-team-${team.id}`}
    >
      {actions && actions.length > 0 ? (
        <div className="org-chart__menu">
          <RowActionsMenu
            actions={actions}
            ariaLabel={`Actions for ${team.name}`}
            testId={`org-tree-team-actions-${team.id}`}
          />
        </div>
      ) : null}
      <span className="org-chart__eyebrow">Team</span>
      <span className="org-chart__name" title={team.name}>
        {team.name}
      </span>
      {count === 0 ? (
        <span className="org-chart__position">No members yet</span>
      ) : (
        <details
          open={open}
          onToggle={(e) => {
            if (!hasMatch) setUserOpen(e.currentTarget.open)
          }}
        >
          <summary>
            {count} {count === 1 ? 'person' : 'people'}
          </summary>
          <Members members={team.members} />
        </details>
      )}
    </div>
  )
}

function TeamNode({ team }: { team: OrgTreeTeamNode }): JSX.Element {
  return (
    <li>
      <TeamCard team={team} />
    </li>
  )
}

function UnitNode({ unit }: { unit: OrgTreeUnitNode }): JSX.Element {
  const { search, unitActions } = useContext(OrgChartContext)
  const hasChildren = unit.childUnits.length > 0 || unit.teams.length > 0
  const onPath = search.active && search.unitOnPath.has(unit.id)
  const headMatch =
    search.active && unit.head != null && search.matchIds.has(unit.head.id)
  const dimmed = search.active && !onPath
  const actions = unitActions?.(unit)
  return (
    <li>
      <div
        className={
          `org-chart__node org-chart__node--${unit.unitType}` +
          (headMatch ? ' is-match' : '') +
          (dimmed ? ' is-dimmed' : '') +
          (actions && actions.length > 0 ? ' org-chart__node--has-menu' : '')
        }
        data-testid={`org-tree-unit-${unit.id}`}
      >
        {actions && actions.length > 0 ? (
          <div className="org-chart__menu">
            <RowActionsMenu
              actions={actions}
              ariaLabel={`Actions for ${unit.name}`}
              testId={`org-tree-unit-actions-${unit.id}`}
            />
          </div>
        ) : null}
        {unit.head ? (
          <>
            {/* Person-first: unit as eyebrow, then name, then position. */}
            <span
              className="org-chart__eyebrow"
              title={`${unit.name} · ${UNIT_TYPE_LABEL[unit.unitType]}`}
            >
              {unit.name} · {UNIT_TYPE_LABEL[unit.unitType]}
            </span>
            <span className="org-chart__name" title={unit.head.name}>
              {unit.head.name}
            </span>
            <span className="org-chart__position" title={unit.head.jobTitle}>
              {unit.head.jobTitle}
            </span>
          </>
        ) : (
          <>
            <span className="org-chart__eyebrow">
              {UNIT_TYPE_LABEL[unit.unitType]}
            </span>
            <span className="org-chart__name" title={unit.name}>
              {unit.name}
            </span>
          </>
        )}
      </div>
      {hasChildren ? (
        <ul>
          {unit.childUnits.map((child) => (
            <UnitNode key={child.id} unit={child} />
          ))}
          {unit.teams.map((team) => (
            <TeamNode key={team.id} team={team} />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export function OrgTreeView({
  tree,
  resolveApprover = () => null,
  candidates = [],
  onSetApprover,
  unitActions,
  teamActions,
  teamCandidates = [],
  onSetTeam,
  onRemoveFromTeam,
  unassignedTeamId,
}: {
  tree: OrgTree
  resolveApprover?: ApproverResolver
  candidates?: ApproverCandidate[]
  onSetApprover?: (employeeId: string, approverId: string) => Promise<void>
  /** ⋮ menu for unit boxes. Omit to keep units read-only. */
  unitActions?: (unit: OrgTreeUnitNode) => RowAction[]
  /** ⋮ menu for team boxes. Omit to keep teams read-only. */
  teamActions?: (team: OrgTreeTeamNode) => RowAction[]
  /** Teams pickable in the popover's "Change team" control. */
  teamCandidates?: ApproverCandidate[]
  /** Persist a person's new team. Omit to hide the popover's team editor. */
  onSetTeam?: (employeeId: string, teamId: string) => Promise<void>
  /** Move a person to the 'Unassigned' holder team. Omit to hide "Remove
   *  from team". */
  onRemoveFromTeam?: (employeeId: string) => Promise<void>
  /** The 'Unassigned' holder team id (hides remove for someone already in it). */
  unassignedTeamId?: string
}): JSX.Element {
  const [selection, setSelection] = useState<Selection | null>(null)
  const [query, setQuery] = useState('')
  const [bulk, setBulk] = useState<{ open: boolean; epoch: number }>({
    open: false,
    epoch: 0,
  })

  // ── Zoom ──────────────────────────────────────────────────────────────────
  // transform: scale on the canvas; a sized wrapper mirrors the scaled
  // dimensions so the scroll region tracks the zoom (transforms alone don't
  // affect layout size). `natural` is the unscaled content box, remeasured on
  // any content change via ResizeObserver.
  const scrollRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const [view, setView] = useState<'td' | 'lr'>('td')
  // Maximize: pin the whole panel to the viewport (a CSS overlay, not the
  // native Fullscreen API, so it works without a user-gesture permission and
  // keeps app theming). Escape exits (once any open popover has closed).
  const [maximized, setMaximized] = useState(false)
  // Latest zoom for the mount-once wheel handler to read without re-binding.
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom

  useEffect(() => {
    const el = canvasRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = (): void =>
      setNatural({ w: el.offsetWidth, h: el.offsetHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Escape exits maximize — but only when no popover is open, so the first
  // Escape closes the popover and the second leaves fullscreen.
  useEffect(() => {
    if (!maximized) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !selection) setMaximized(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [maximized, selection])

  const setZoomClamped = (z: number): void =>
    setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100)))

  const fitToWidth = (): void => {
    const scroller = scrollRef.current
    if (!scroller || !natural || natural.w === 0) return
    const avail = scroller.clientWidth - 32 // minus container padding
    setZoomClamped(Math.min(1, avail / natural.w))
  }

  // Ctrl/⌘ + wheel to zoom. Native non-passive listener so preventDefault
  // works (React's onWheel is passive and can't stop the page scroll).
  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return
    const onWheel = (e: WheelEvent): void => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      setZoomClamped(zoomRef.current - e.deltaY * 0.0015)
    }
    scroller.addEventListener('wheel', onWheel, { passive: false })
    return () => scroller.removeEventListener('wheel', onWheel)
  }, [])

  // Derive which employees match, which teams contain a match, and which
  // units sit on the root→match path — so we can dim everything else and
  // force-open matched teams.
  const search = useMemo<SearchState>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return EMPTY_SEARCH
    const matchIds = new Set<string>()
    const teamHasMatch = new Set<string>()
    const unitOnPath = new Set<string>()
    const hit = (name: string): boolean => name.toLowerCase().includes(q)

    const walkTeam = (t: OrgTreeTeamNode): boolean => {
      let found = false
      for (const m of t.members) {
        if (hit(m.name)) {
          matchIds.add(m.id)
          found = true
        }
      }
      if (found) teamHasMatch.add(t.id)
      return found
    }
    const walkUnit = (u: OrgTreeUnitNode): boolean => {
      let onPath = u.head != null && hit(u.head.name)
      if (u.head && onPath) matchIds.add(u.head.id)
      for (const c of u.childUnits) if (walkUnit(c)) onPath = true
      for (const t of u.teams) if (walkTeam(t)) onPath = true
      if (onPath) unitOnPath.add(u.id)
      return onPath
    }
    for (const r of tree.roots) walkUnit(r)
    for (const t of tree.orphanTeams) walkTeam(t)
    return { active: true, matchIds, teamHasMatch, unitOnPath }
  }, [query, tree])

  const ctx: OrgChartCtx = {
    resolveApprover,
    onSelectMember: (emp, anchor) => setSelection({ emp, anchor }),
    candidates,
    search,
    bulk,
    teamCandidates,
    ...(onSetApprover ? { onSetApprover } : {}),
    ...(unitActions ? { unitActions } : {}),
    ...(teamActions ? { teamActions } : {}),
    ...(onSetTeam ? { onSetTeam } : {}),
    ...(onRemoveFromTeam ? { onRemoveFromTeam } : {}),
    ...(unassignedTeamId ? { unassignedTeamId } : {}),
  }
  const panel = (
    <OrgChartContext.Provider value={ctx}>
    <div
      ref={scrollRef}
      data-testid="org-tree"
      data-maximized={maximized ? 'true' : undefined}
      style={{
        border: maximized ? 'none' : '1px solid var(--border)',
        borderRadius: maximized ? 0 : 14,
        padding: 16,
        background: 'var(--surface)',
        boxSizing: 'border-box',
        overflow: 'auto',
        // Maximized → pin to the viewport as a full-screen overlay; otherwise
        // fill the viewport minus the app header + page head. ONE scroll
        // region for the whole panel (no confusing nested chart scrollbar).
        ...(maximized
          ? {
              position: 'fixed' as const,
              inset: 0,
              height: '100vh',
              width: '100vw',
              // Portaled to <body>, so this z-index is a body-level layer.
              // Sit ABOVE the app shell (TopBar/AppShell z=20, ViewingAsBar
              // z=30) but BELOW the popover (z=41), Modal (z=100), and the
              // kebab/combobox panels (z=1000) — otherwise the "change
              // approver / add team" dialogs open *behind* the full screen.
              zIndex: 35,
            }
          : { height: 'calc(100vh - 150px)' }),
      }}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: CHART_STYLES }} />

      <div className="org-chart-toolbar">
        <span className="org-chart-search" aria-hidden>
          🔍
        </span>
        <input
          type="search"
          className="org-chart-search__input"
          data-testid="org-tree-search"
          placeholder="Search employee name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="button"
          className="org-chart-search__toggle"
          data-testid="org-tree-expand-toggle"
          aria-pressed={bulk.open}
          onClick={() =>
            setBulk((b) => ({ open: !b.open, epoch: b.epoch + 1 }))
          }
        >
          <span className="org-chart-search__toggle-label">
            {bulk.open ? 'Collapse all' : 'Expand all'}
          </span>
          <span
            aria-hidden
            className="org-chart-search__caret"
            style={{ transform: bulk.open ? 'rotate(90deg)' : 'none' }}
          >
            ▸
          </span>
        </button>
        {search.active ? (
          <span className="org-chart-search__count">
            {search.matchIds.size}{' '}
            {search.matchIds.size === 1 ? 'match' : 'matches'}
          </span>
        ) : null}

        <div
          className="org-chart-viewtoggle"
          role="group"
          aria-label="Chart layout"
          data-testid="org-tree-view"
        >
          <button
            type="button"
            className={`org-chart-viewtoggle__btn${view === 'td' ? ' is-active' : ''}`}
            aria-pressed={view === 'td'}
            data-testid="org-tree-view-td"
            onClick={() => setView('td')}
          >
            Top-down
          </button>
          <button
            type="button"
            className={`org-chart-viewtoggle__btn${view === 'lr' ? ' is-active' : ''}`}
            aria-pressed={view === 'lr'}
            data-testid="org-tree-view-lr"
            onClick={() => setView('lr')}
          >
            Left-to-right
          </button>
        </div>

        <div className="org-chart-zoom" data-testid="org-tree-zoom">
          <button
            type="button"
            className="org-chart-zoom__btn"
            aria-label="Zoom out"
            data-testid="org-tree-zoom-out"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => setZoomClamped(zoom - ZOOM_STEP)}
          >
            −
          </button>
          <button
            type="button"
            className="org-chart-zoom__pct"
            aria-label="Reset zoom to 100%"
            data-testid="org-tree-zoom-reset"
            onClick={() => setZoom(1)}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="org-chart-zoom__btn"
            aria-label="Zoom in"
            data-testid="org-tree-zoom-in"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => setZoomClamped(zoom + ZOOM_STEP)}
          >
            +
          </button>
          <button
            type="button"
            className="org-chart-zoom__fit"
            data-testid="org-tree-zoom-fit"
            onClick={fitToWidth}
          >
            Fit
          </button>
          <button
            type="button"
            className="org-chart-zoom__fit org-chart-zoom__full"
            data-testid="org-tree-fullscreen"
            aria-pressed={maximized}
            title={maximized ? 'Exit full screen (Esc)' : 'Full screen'}
            onClick={() => setMaximized((m) => !m)}
          >
            {maximized ? '↙ Exit' : '⤢ Full screen'}
          </button>
        </div>
      </div>

      {/* Sizer mirrors the scaled dimensions so the scroll region tracks the
          zoom; the canvas is the actual scaled content. */}
      <div
        style={
          natural
            ? { width: natural.w * zoom, height: natural.h * zoom }
            : undefined
        }
      >
        <div
          ref={canvasRef}
          className="org-chart-canvas"
          style={{ transform: `scale(${zoom})`, transformOrigin: '0 0' }}
        >
          <div className={`org-chart${view === 'lr' ? ' org-chart--lr' : ''}`}>
            <ul>
              {tree.roots.map((root) => (
                <UnitNode key={root.id} unit={root} />
              ))}
            </ul>
          </div>

          {tree.orphanTeams.length > 0 ? (
            <section data-testid="org-tree-unassigned" className="org-tree-unassigned">
              <div className="org-tree-unassigned__title">
                Unassigned teams
                <span className="org-tree-unassigned__hint">
                  not yet placed under a unit
                </span>
              </div>
              <div className="org-chart org-tree-unassigned__cards">
                {tree.orphanTeams.map((team) => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {selection ? (
        <ApproverPopover
          selection={selection}
          resolveApprover={resolveApprover}
          onClose={() => setSelection(null)}
        />
      ) : null}
    </div>
    </OrgChartContext.Provider>
  )
  // Maximized → portal to <body> so `position: fixed` is relative to the
  // viewport (a transformed app-shell ancestor would otherwise trap it,
  // leaving the sidebar/header showing). Normal → render in place.
  return maximized ? createPortal(panel, document.body) : panel
}
